import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { invalidateReadiness, invalidateAlerts } from "@/lib/cacheInvalidators";

const UnreadChatCtx = createContext({
  unreadTripIds: new Set(),
  hasUnread: (_tripId) => false,
  markRead: (_tripId) => {},
  refresh: () => {},
});

export function UnreadChatProvider({ children }) {
  const { user } = useAuth();
  const [unreadTripIds, setUnreadTripIds] = useState(new Set());
  const wsRef = useRef(null);
  // Bug 30 audit: en sesión manual vi decenas de /unread-chat-trips por minuto
  // muchos con ERR_ABORTED. El polling base es 60s, pero el WS handler también
  // dispara refresh() por cada chat_message; un burst de mensajes generaba
  // fan-out de requests concurrentes que se pisaban. Estas refs centralizan:
  //   - inFlightRef: AbortController del request actual para cancelar el
  //     previo si llega uno nuevo (en vez de tener 5 en vuelo a la vez)
  //   - debounceRef: agrupa refresh() consecutivos dentro de 500ms en un
  //     solo request (típico cuando llegan varios chat_message juntos).
  const inFlightRef = useRef(null);
  const debounceRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    // Cancelar request anterior si todavía está en vuelo.
    if (inFlightRef.current) {
      try {
        inFlightRef.current.abort();
      } catch {}
    }
    const controller = new AbortController();
    inFlightRef.current = controller;
    try {
      const { data } = await api.get("/unread-chat-trips", { signal: controller.signal });
      setUnreadTripIds(new Set(data?.trip_ids || []));
    } catch (err) {
      // Cancelaciones explícitas no son errores reales — solo loguear el resto.
      if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
        console.error("UnreadChatContext: /unread-chat-trips refresh failed", err);
      }
    } finally {
      if (inFlightRef.current === controller) inFlightRef.current = null;
    }
  }, [user]);

  // Debounce: agrupa llamadas rápidas (ej. ráfaga de chat_message via WS) en
  // un solo refresh() después de 500ms de quietud.
  const scheduleRefresh = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      refresh();
    }, 500);
  }, [refresh]);

  // Locally mark a trip as read so the badge clears instantly when the user
  // opens the chat. Server-side marking is done by TripChat via /chat-read.
  const markRead = useCallback((tripId) => {
    if (!tripId) return;
    setUnreadTripIds((prev) => {
      if (!prev.has(tripId)) return prev;
      const next = new Set(prev);
      next.delete(tripId);
      return next;
    });
  }, []);

  const hasUnread = useCallback((tripId) => unreadTripIds.has(tripId), [unreadTripIds]);

  // Initial fetch + 60s safety-net poll (WS below is the primary live path).
  // Bug 30 audit: pausamos polling cuando el tab está oculto — el usuario no
  // ve el badge igual y desperdicia CPU + cuota de API. visibilitychange
  // dispara refresh inmediato al volver para no quedar con badge frío.
  useEffect(() => {
    if (!user) return;
    refresh();
    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(refresh, 60000);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        refresh();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [user, refresh]);

  // Live WS — when the backend emits any notification, re-fetch the list.
  // Cheap (<1KB), and keeps badges in sync without per-message wiring.
  useEffect(() => {
    if (!user) return;
    const apiUrl = import.meta.env.VITE_BACKEND_URL || "";
    const wsBase = apiUrl.replace(/^http/, "ws");
    if (!wsBase) return;

    let closed = false;
    let retryTimer;
    let pingTimer;
    let attempts = 0;
    const MAX_ATTEMPTS = 8; // ~exp backoff caps around 2 min before giving up

    const connect = () => {
      // Read token fresh on every (re)connect — it may have rotated since mount.
      const token = localStorage.getItem("altiv_token");
      if (!token) return;
      let ws;
      try {
        ws = new WebSocket(`${wsBase}/api/notifications/ws?token=${encodeURIComponent(token)}`);
      } catch {
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        attempts = 0;
        // Heartbeat so proxies (Render/Cloudflare) don't kill the idle WS.
        clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send("ping");
            } catch {}
          }
        }, 30000);
      };
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg?.kind === "notification" && msg?.type === "chat_message") {
            scheduleRefresh();
          }
          // OCC live-refresh: backend pushes this when an owner creates,
          // edits, cancels a trip or updates a passenger. Coordinator with
          // the OCC pinned sees the change without waiting for the 5-min TTL.
          if (msg?.event === "readiness_invalidate") {
            invalidateReadiness();
            invalidateAlerts();
            // Re-broadcast to any open component (e.g. FlightDetailModal) so it
            // can refetch its local snapshot when the mutation came from outside
            // — FRE engine action, another tab, another user. Cache invalidation
            // alone won't repaint an already-mounted modal.
            try {
              window.dispatchEvent(
                new CustomEvent("altiv:readiness-invalidate", {
                  detail: { scope: msg.scope, ref_id: msg.ref_id },
                })
              );
            } catch {}
          }
        } catch {}
      };
      ws.onclose = () => {
        clearInterval(pingTimer);
        if (closed) return;
        if (attempts >= MAX_ATTEMPTS) return; // poll fallback takes over
        attempts += 1;
        const delay = Math.min(30000, 1000 * Math.pow(2, attempts));
        retryTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {}
      };
    };
    connect();

    return () => {
      closed = true;
      clearTimeout(retryTimer);
      clearInterval(pingTimer);
      try {
        wsRef.current && wsRef.current.close();
      } catch {}
    };
  }, [user, refresh, scheduleRefresh]);

  return (
    <UnreadChatCtx.Provider value={{ unreadTripIds, hasUnread, markRead, refresh }}>
      {children}
    </UnreadChatCtx.Provider>
  );
}

export const useUnreadChat = () => useContext(UnreadChatCtx);
